import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faClipboardCheck, faBell, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { POLICIES } from 'Config';

interface BottomTabsProps {
    onMoreClick?: () => void;
}

interface BottomTab {
    label: string;
    icon: any;
    url: string | null;
    policies?: (keyof typeof POLICIES)[];
    policyType?: 'OR' | 'AND';
}

const BottomTabs = ({ onMoreClick }: BottomTabsProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);

    const tabs = useMemo<BottomTab[]>(() => {
        const all: BottomTab[] = [
            {
                label: t('menu.device_checklist'),
                icon: faClipboardCheck,
                url: '/device-checklist',
                policies: ['MY_VEHICLES_READ'],
                policyType: 'OR',
            },
            {
                label: t('menu.my_vehicles'),
                icon: faCar,
                url: '/vehicles',
                policies: ['MY_VEHICLES_READ', 'MY_VEHICLES_WRITE'],
                policyType: 'OR',
            },
            {
                label: t('menu.alarm_settings'),
                icon: faBell,
                url: '/alarmsettings',
                policies: ['ALARM_SETTINGS_READ', 'ALARM_SETTINGS_WRITE'],
                policyType: 'OR',
            },
            {
                label: t('common.more') || 'Mais',
                icon: faEllipsis,
                url: null,
            },
        ];
        const userPolicies = loggedUser?.policies ?? [];
        return all.filter(tab => {
            if (!tab.policies || tab.policies.length === 0) return true;
            return UsersService.hasPolicies(userPolicies, tab.policies, tab.policyType ?? 'OR');
        });
    }, [loggedUser, t]);

    const isActive = (url: string | null) => {
        if (!url) return false;
        return location.pathname === url || location.pathname.startsWith(url + '/');
    };

    return (
        <nav
            className="fixed bottom-0 inset-x-0 z-30 sm:hidden flex"
            style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderTop: '1px solid #f1f5f9' }}
        >
            {tabs.map((tab, i) => {
                const active = isActive(tab.url);
                return (
                    <button
                        key={i}
                        onClick={() => {
                            if (tab.url) {
                                navigate(tab.url);
                            } else if (onMoreClick) {
                                onMoreClick();
                            }
                        }}
                        className={`
                            flex-1 flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors border-0 bg-transparent
                            ${active ? 'text-sky-500' : 'text-slate-400'}
                        `}
                    >
                        <div className={`w-10 h-6 flex items-center justify-center rounded-2xl transition-all ${active ? 'bg-sky-100' : ''}`}>
                            <FontAwesomeIcon icon={tab.icon} className="text-base" />
                        </div>
                        {tab.label}
                    </button>
                );
            })}
        </nav>
    );
};

export default BottomTabs;
